import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useProfile } from '@/hooks/useProfile';
import { useIsMobile } from '@/hooks/use-mobile';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { MessageSquare, MessagesSquare, Plus, Search, Settings, Loader2, Trash2, Send, RefreshCw, Pin, PinOff, Bell, BellOff, Archive, ArchiveRestore, ArrowLeft, CircleDot } from 'lucide-react';

// Per-user localStorage key for chat list cache
const getChatCacheKey = (userId: string) => `chat_list_v1_${userId}`;
import { GlobalMessageSearch } from '@/components/GlobalMessageSearch';
import { StatusDialog } from '@/components/StatusDialog';
import { Badge } from '@/components/ui/badge';
import { formatDistanceToNow } from 'date-fns';
import { useToast } from '@/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface Chat {
  id: string;
  is_group: boolean;
  name?: string;
  created_at: string;
  is_pinned: boolean;
  is_muted: boolean;
  is_archived: boolean;
  last_message?: {
    id: string;
    text: string;
    translated_texts?: Record<string, string> | null;
    sender_id: string;
    sender_language?: string | null;
    created_at: string;
  };
  other_participant?: {
    id: string;
    name: string;
    profile_image?: string;
    is_online: boolean;
  };
  unread_count: number;
}

interface PendingInvitation {
  id: string;
  invited_phone: string;
  created_at: string;
  delivery_status?: string | null;
  twilio_error_code?: string | null;
}

interface RealtimeMessagePayload {
  id: string;
  chat_id: string;
  sender_id: string;
  original_text: string;
  translated_texts?: Record<string, string> | null;
  sender_language?: string | null;
  created_at: string;
}

const ChatList = () => {
  const isMobile = useIsMobile();
  const [chats, setChats] = useState<Chat[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<PendingInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  // Never leave the chat list spinning forever if a request stalls.
  useEffect(() => {
    if (!loading) return;
    const t = setTimeout(() => setLoading(false), 10000);
    return () => clearTimeout(t);
  }, [loading]);
  const [showStatus, setShowStatus] = useState(false);
  const [statusUserId, setStatusUserId] = useState<string | undefined>(undefined);
  const [activeStatusUserIds, setActiveStatusUserIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [deletingChatId, setDeletingChatId] = useState<string | null>(null);
  const [chatToDelete, setChatToDelete] = useState<Chat | null>(null);
  const [resendingInviteId, setResendingInviteId] = useState<string | null>(null);
  const [inviteToCancel, setInviteToCancel] = useState<PendingInvitation | null>(null);
  const [cancellingInviteId, setCancellingInviteId] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const chatsFetchRequestRef = useRef(0);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile } = useProfile();
  const { toast } = useToast();
  const { t, language } = useLanguage();
  // On desktop, DesktopChatLayout handles the list.
  // IMPORTANT: do not early-return before hooks (can break hook order when isMobile changes).
  const showDesktopPlaceholder = !isMobile;

  const applyIncomingMessage = (message: RealtimeMessagePayload) => {
    if (!user) return;

    setChats((prev) => {
      const chatIndex = prev.findIndex((chat) => chat.id === message.chat_id);

      if (chatIndex === -1) {
        void fetchChats();
        return prev;
      }

      const targetChat = prev[chatIndex];
      if (targetChat.last_message?.id === message.id) {
        return prev;
      }

      const updatedChat: Chat = {
        ...targetChat,
        last_message: {
          id: message.id,
          text: message.original_text,
          translated_texts: message.translated_texts,
          sender_id: message.sender_id,
          sender_language: message.sender_language,
          created_at: message.created_at,
        },
        unread_count:
          targetChat.unread_count +
          (message.sender_id === user.id || targetChat.is_muted ? 0 : 1),
      };

      const updatedChats = [...prev];
      updatedChats[chatIndex] = updatedChat;
      updatedChats.sort((a, b) => {
        if (a.is_pinned && !b.is_pinned) return -1;
        if (!a.is_pinned && b.is_pinned) return 1;
        const aTime = a.last_message?.created_at || a.created_at;
        const bTime = b.last_message?.created_at || b.created_at;
        return new Date(bTime).getTime() - new Date(aTime).getTime();
      });

      return updatedChats;
    });
  };

  // Hydrate from cache on first render so the list shows instantly
  useEffect(() => {
    if (!user || showDesktopPlaceholder) return;
    try {
      const raw = localStorage.getItem(getChatCacheKey(user.id));
      if (raw) {
        const cached = JSON.parse(raw) as Chat[];
        if (cached.length > 0) {
          setChats(cached);
          setLoading(false);
        }
      }
    } catch {
      // Ignore parse errors — fresh fetch will follow
    }
  }, [user?.id, showDesktopPlaceholder]);

  useEffect(() => {
    // Desktop placeholder: don't subscribe/fetch.
    if (showDesktopPlaceholder) return;

    if (!user) {
      navigate('/auth');
      return;
    }

    let initialLoadTriggered = false;
    const loadInitialData = () => {
      if (initialLoadTriggered) return;
      initialLoadTriggered = true;
      void fetchChats();
      void fetchPendingInvitations();
      void fetchActiveStatuses();
    };

    const resyncData = () => {
      void fetchChats();
      void fetchPendingInvitations();
    };

    const channel = supabase
      .channel(`chat-updates-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
        },
        (payload) => {
          applyIncomingMessage(payload.new as RealtimeMessagePayload);
          void fetchChats();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'messages',
        },
        () => {
          void fetchChats();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'invitations',
        },
        () => {
          void fetchPendingInvitations();
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          loadInitialData();
          return;
        }

        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          resyncData();
        }
      });

    const fallbackLoadTimer = setTimeout(loadInitialData, 1500);
    const periodicSyncTimer = window.setInterval(() => {
      void fetchChats();
    }, 6000);

    const handleFocus = () => {
      resyncData();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        resyncData();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    let appStateListener: { remove: () => Promise<void> } | null = null;
    let effectActive = true;

    if (Capacitor.isNativePlatform()) {
      void CapacitorApp.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
          resyncData();
        }
      }).then((listener) => {
        if (effectActive) {
          appStateListener = listener;
          return;
        }

        void listener.remove();
      });
    }

    return () => {
      effectActive = false;
      clearTimeout(fallbackLoadTimer);
      clearInterval(periodicSyncTimer);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);

      if (appStateListener) {
        void appStateListener.remove();
      }

      supabase.removeChannel(channel);
    };
  }, [user, navigate, showDesktopPlaceholder]);

  const fetchActiveStatuses = async () => {
    if (!user) return;
    try {
      const { data } = await supabase
        .from('statuses')
        .select('user_id')
        .gt('expires_at', new Date().toISOString());
      if (data) {
        setActiveStatusUserIds(new Set(data.map((s) => s.user_id)));
      }
    } catch (e) {
      console.error('[ChatList] fetchActiveStatuses failed', e);
    }
  };

  const fetchChats = async () => {
    if (!user) return;
    const requestId = ++chatsFetchRequestRef.current;

    try {
      const { data: participantData, error: participantError } = await supabase
        .from('chat_participants')
        .select(`
          chat_id,
          is_pinned,
          is_muted,
          is_archived,
          chats (
            id,
            is_group,
            name,
            created_at
          )
        `)
        .eq('user_id', user.id);

      if (participantError) throw participantError;

      const chatIds = participantData.map((p) => p.chat_id);

      const chatsWithMessages = await Promise.all(
        participantData.map(async (p) => {
          const chat = p.chats;
          
          const { data: messageData } = await supabase
            .from('messages')
            .select('id, original_text, translated_texts, sender_language, created_at, sender_id')
            .eq('chat_id', chat.id)
            .order('created_at', { ascending: false })
            .limit(1)
            .single();

          const { count: unreadCount } = await supabase
            .from('messages')
            .select('*', { count: 'exact', head: true })
            .eq('chat_id', chat.id)
            .eq('is_read', false)
            .neq('sender_id', user.id);

          let otherParticipant = null;
          if (!chat.is_group) {
            // Get other participant's user_id first
            const { data: otherParticipantData } = await supabase
              .from('chat_participants')
              .select('user_id')
              .eq('chat_id', chat.id)
              .neq('user_id', user.id)
              .maybeSingle();

            if (otherParticipantData) {
              // Use RPC function to get profile (bypasses RLS restrictions)
              const { data: profileData } = await supabase
                .rpc('get_chat_participant_profile', { _profile_id: otherParticipantData.user_id });
              
              if (profileData && profileData.length > 0) {
                otherParticipant = profileData[0];
              }
            }
          }

          return {
            ...chat,
            is_pinned: p.is_pinned || false,
            is_muted: p.is_muted || false,
            is_archived: p.is_archived || false,
            last_message: messageData
              ? {
                  id: messageData.id,
                  text: messageData.original_text,
                  translated_texts: messageData.translated_texts as Record<string, string> | null,
                  sender_id: messageData.sender_id,
                  sender_language: messageData.sender_language,
                  created_at: messageData.created_at,
                }
              : undefined,
            other_participant: otherParticipant,
            unread_count: unreadCount || 0,
          };
        })
      );

      if (requestId !== chatsFetchRequestRef.current) return;

      chatsWithMessages.sort((a, b) => {
        // Pinned chats first
        if (a.is_pinned && !b.is_pinned) return -1;
        if (!a.is_pinned && b.is_pinned) return 1;
        const aTime = a.last_message?.created_at || a.created_at;
        const bTime = b.last_message?.created_at || b.created_at;
        return new Date(bTime).getTime() - new Date(aTime).getTime();
      });

      setChats(chatsWithMessages);

      // Persist to cache so the next app open feels instant
      try {
        localStorage.setItem(getChatCacheKey(user.id), JSON.stringify(chatsWithMessages));
      } catch {
        // Storage quota exceeded — not critical
      }

      // Background-translate any previews that aren't yet in the user's language.
      // This mirrors ChatScreen's translateMissingMessages pattern so the list
      // shows translated text even before the chat is opened for the first time.
      void translateMissingPreviews(chatsWithMessages);
    } catch (error) {
      console.error('Error fetching chats:', error);
    } finally {
      if (requestId === chatsFetchRequestRef.current) {
        setLoading(false);
      }
    }
  };

  const translateMissingPreviews = async (chatList: Chat[]) => {
    if (!user) return;
    const preferredLang = profile?.preferred_language || language;
    if (!preferredLang) return;

    const toTranslate = chatList.filter((c) => {
      const msg = c.last_message;
      return (
        msg &&
        msg.sender_id !== user.id &&          // not own message
        msg.sender_language &&                 // source language known
        msg.sender_language !== preferredLang && // different from user's lang
        !(msg.translated_texts?.[preferredLang]) // translation not yet stored
      );
    });

    if (toTranslate.length === 0) return;

    const { data: { session } } = await supabase.auth.getSession();
    const accessToken = session?.access_token;

    await Promise.allSettled(
      toTranslate.map(async (chat) => {
        const msg = chat.last_message!;
        try {
          const { data, error } = await supabase.functions.invoke('translate-message', {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
            body: {
              text: msg.text,
              sourceLanguage: msg.sender_language,
              targetLanguages: [preferredLang],
            },
          });

          const translated = data?.translations?.[preferredLang];
          if (error || !translated) return;

          const nextTranslatedTexts = {
            ...(msg.translated_texts || {}),
            [preferredLang]: translated,
          };

          // Update preview in state immediately
          setChats((prev) =>
            prev.map((c) =>
              c.id === chat.id
                ? { ...c, last_message: { ...c.last_message!, translated_texts: nextTranslatedTexts } }
                : c
            )
          );

          // Persist to DB so subsequent loads don't need to re-translate
          void supabase
            .from('messages')
            .update({ translated_texts: nextTranslatedTexts })
            .eq('id', msg.id);
        } catch (err) {
          console.error('[ChatList] Failed to translate preview for chat', chat.id, err);
        }
      })
    );
  };

  const fetchPendingInvitations = async () => {
    if (!user) return;
    try {
      const { data, error } = await supabase
        .from('invitations')
        .select('id, invited_phone, created_at, delivery_status, twilio_error_code')
        .eq('inviter_id', user.id)
        .eq('processed', false)
        .order('created_at', { ascending: false });

      if (error) throw error;
      // Deduplicate by phone: the query is ordered DESC so the first match is the latest
      const seen = new Set<string>();
      const deduped = (data || []).filter(inv => {
        if (seen.has(inv.invited_phone)) return false;
        seen.add(inv.invited_phone);
        return true;
      });
      setPendingInvitations(deduped);
    } catch (error) {
      console.error('Error fetching pending invitations:', error);
    }
  };

  // Returns the best available preview text for a chat's last message:
  // translated version in the user's language if available, otherwise original.
  const getPreviewText = (lastMessage: Chat['last_message']): string => {
    if (!lastMessage) return '';
    const tt = lastMessage.translated_texts;
    if (tt && typeof tt === 'object' && tt[language]) {
      return tt[language];
    }
    return lastMessage.text;
  };

  const archivedCount = chats.filter(c => c.is_archived).length;
  const archivedUnreadCount = chats
    .filter(c => c.is_archived && !c.is_muted)
    .reduce((sum, c) => sum + (c.unread_count || 0), 0);

  const filteredChats = chats.filter((chat) => {
    // Show archived view: only archived chats; default view: only non-archived
    if (showArchived ? !chat.is_archived : chat.is_archived) return false;
    const searchLower = searchQuery.toLowerCase();
    if (chat.is_group) {
      return chat.name?.toLowerCase().includes(searchLower);
    }
    return chat.other_participant?.name.toLowerCase().includes(searchLower);
  });

  const handleDeleteChat = async () => {
    if (!chatToDelete) return;

    setDeletingChatId(chatToDelete.id);
    try {
      const { error } = await supabase
        .from('chats')
        .delete()
        .eq('id', chatToDelete.id);

      if (error) throw error;

      toast({
        title: t('deleteChat'),
        description: t('success'),
      });

      setChats(chats.filter(c => c.id !== chatToDelete.id));
    } catch (error: any) {
      console.error('Error deleting chat:', error);
      toast({
        title: t('error'),
        description: t('tryAgain'),
        variant: 'destructive',
      });
    } finally {
      setDeletingChatId(null);
      setChatToDelete(null);
    }
  };

  const handleResendInvite = async (invitation: PendingInvitation) => {
    if (!user || resendingInviteId) return;
    setResendingInviteId(invitation.id);
    try {
      // NOTE: We do NOT delete from DB here.
      // Deleting would trigger realtime events that race with the subsequent insert,
      // causing other pending invitations to disappear from the list.
      // Instead we pass isResend:true so the edge function updates the existing row
      // in-place (no new row = no race condition, no duplicates).
      const [{ data: { session } }, { data: profileData }] = await Promise.all([
        supabase.auth.getSession(),
        supabase.from('profiles').select('name').eq('id', user.id).single(),
      ]);

      const { error } = await supabase.functions.invoke('send-sms-invite', {
        headers: session?.access_token
          ? { Authorization: `Bearer ${session.access_token}` }
          : undefined,
        body: {
          phoneNumber: invitation.invited_phone,
          inviterName: profileData?.name || 'A friend',
          isResend: true,
        },
      });

      if (error) {
        // Supabase FunctionsHttpError stores the actual JSON body in error.context
        // (a Response object). JSON.parse(error.message) never works for HTTP errors.
        let errorMessage = t('tryAgain');
        try {
          if (error.context && typeof error.context.json === 'function') {
            const body = await error.context.json();
            if (body?.error) errorMessage = body.error;
          } else if (error.message) {
            errorMessage = error.message;
          }
        } catch {
          if (error.message) errorMessage = error.message;
        }
        throw new Error(errorMessage);
      }

      toast({
        title: t('inviteResent'),
        description: t('inviteSent'),
      });
    } catch (error: any) {
      console.error('Error resending invite:', error);
      toast({
        title: t('error'),
        description: error.message || t('tryAgain'),
        variant: 'destructive',
      });
    } finally {
      setResendingInviteId(null);
    }
  };

  const handleCancelInvite = async () => {
    if (!inviteToCancel) return;
    setCancellingInviteId(inviteToCancel.id);
    try {
      const { error } = await supabase
        .from('invitations')
        .delete()
        .eq('id', inviteToCancel.id);

      if (error) throw error;

      setPendingInvitations(prev => prev.filter(i => i.id !== inviteToCancel.id));
      toast({
        title: t('inviteCancelled'),
      });
    } catch (error) {
      console.error('Error cancelling invite:', error);
      toast({
        title: t('error'),
        description: t('tryAgain'),
        variant: 'destructive',
      });
    } finally {
      setCancellingInviteId(null);
      setInviteToCancel(null);
    }
  };

  const togglePinChat = async (chatId: string, currentPinned: boolean) => {
    if (!user) return;
    try {
      const { error } = await supabase
        .from('chat_participants')
        .update({ is_pinned: !currentPinned })
        .eq('chat_id', chatId)
        .eq('user_id', user.id);

      if (error) throw error;
      setChats(prev => {
        const updated = prev.map(c => c.id === chatId ? { ...c, is_pinned: !currentPinned } : c);
        updated.sort((a, b) => {
          if (a.is_pinned && !b.is_pinned) return -1;
          if (!a.is_pinned && b.is_pinned) return 1;
          const aTime = a.last_message?.created_at || a.created_at;
          const bTime = b.last_message?.created_at || b.created_at;
          return new Date(bTime).getTime() - new Date(aTime).getTime();
        });
        return updated;
      });
    } catch (error) {
      console.error('Error toggling pin:', error);
    }
  };

  const toggleMuteChat = async (chatId: string, currentMuted: boolean) => {
    if (!user) return;
    try {
      const { error } = await supabase
        .from('chat_participants')
        .update({ is_muted: !currentMuted })
        .eq('chat_id', chatId)
        .eq('user_id', user.id);

      if (error) throw error;
      setChats(prev =>
        prev.map(c => c.id === chatId ? { ...c, is_muted: !currentMuted } : c)
      );
    } catch (error) {
      console.error('Error toggling mute:', error);
    }
  };

  const toggleArchiveChat = async (chatId: string, currentArchived: boolean) => {
    if (!user) return;
    try {
      const { error } = await supabase
        .from('chat_participants')
        .update({ is_archived: !currentArchived })
        .eq('chat_id', chatId)
        .eq('user_id', user.id);

      if (error) throw error;
      setChats(prev =>
        prev.map(c => c.id === chatId ? { ...c, is_archived: !currentArchived } : c)
      );
      toast({
        title: !currentArchived ? t('archiveChat') : t('unarchiveChat'),
      });
    } catch (error) {
      console.error('Error toggling archive:', error);
    }
  };

  if (showDesktopPlaceholder) {
    return (
      <div className="flex-1 flex items-center justify-center bg-muted/20">
        <div className="text-center">
          <MessageSquare className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
          <h1 className="text-xl font-semibold mb-2">{t('noChats')}</h1>
          <p className="text-muted-foreground">{t('startChatting')}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="safe-area-screen flex flex-col bg-background overflow-hidden">
      <div className="border-b bg-card pt-[max(env(safe-area-inset-top),0.75rem)] flex-shrink-0">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {showArchived && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowArchived(false)}
                title={t('hideArchived')}
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
            )}
            <Avatar>
              <AvatarImage src={profile?.profile_image} />
              <AvatarFallback>{profile?.name?.[0]}</AvatarFallback>
            </Avatar>
            <div>
              <h1 className="font-semibold">
                {showArchived ? t('archivedChats') : profile?.name}
              </h1>
              <p className="text-sm text-muted-foreground">Global Chat Connect</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {!showArchived && archivedCount > 0 && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowArchived(true)}
                title={t('showArchived')}
                className="relative"
              >
                <Archive className="h-5 w-5" />
                {archivedUnreadCount > 0 && (
                  <Badge
                    variant="default"
                    className="absolute -top-1 -right-1 h-4 min-w-4 px-1 text-[10px]"
                  >
                    {archivedUnreadCount}
                  </Badge>
                )}
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowStatus(true)}
              title="Status"
            >
              <CircleDot className="h-5 w-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/settings')}
            >
              <Settings className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-4 flex-shrink-0">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('search')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <GlobalMessageSearch searchQuery={searchQuery} />
      </div>

      <ScrollArea className="flex-1 max-w-2xl mx-auto w-full">
        {filteredChats.length === 0 && (showArchived || pendingInvitations.length === 0) ? (
          <div className="text-center py-12 px-4">
            <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-semibold text-lg mb-2">
              {showArchived ? t('noArchivedChats') : t('noChats')}
            </h3>
            {!showArchived && (
              <>
                <p className="text-muted-foreground mb-4">
                  {t('startChatting')}
                </p>
                <Button onClick={() => navigate('/add-contact')}>
                  <Plus className="h-4 w-4 mr-2" />
                  {t('addContact')}
                </Button>
              </>
            )}
          </div>
        ) : (
          <div className="divide-y overflow-x-hidden">
            {filteredChats.map((chat) => (
              <div
                key={chat.id}
                className="flex items-center gap-2 px-3 py-3 hover:bg-muted/50 transition-colors w-full"
              >
                <button
                  onClick={() => navigate(`/chat/${chat.id}`)}
                  className="flex items-center gap-3 flex-1 text-left"
                >
                  {/* Avatar with optional story ring */}
                  {!chat.is_group && chat.other_participant?.id && activeStatusUserIds.has(chat.other_participant.id) ? (
                    <button
                      type="button"
                      className="rounded-full p-[2px] bg-gradient-to-tr from-primary to-purple-500 flex-shrink-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        setStatusUserId(chat.other_participant!.id);
                        setShowStatus(true);
                      }}
                      title="View status"
                    >
                      <Avatar className="border-2 border-background">
                        <AvatarImage src={chat.other_participant?.profile_image} />
                        <AvatarFallback>{chat.other_participant?.name[0]}</AvatarFallback>
                      </Avatar>
                    </button>
                  ) : (
                    <Avatar>
                      <AvatarImage src={chat.other_participant?.profile_image} />
                      <AvatarFallback>
                        {chat.is_group
                          ? chat.name?.[0]
                          : chat.other_participant?.name[0]}
                      </AvatarFallback>
                    </Avatar>
                  )}
                  <div className="flex-1 min-w-0">
                    {/* Row 1: name (left) + timestamp (right) */}
                    <div className="flex items-center justify-between gap-2 mb-0.5">
                      <div className="flex items-center gap-1.5 min-w-0">
                        {chat.is_pinned && <Pin className="h-3 w-3 text-primary flex-shrink-0" />}
                        {chat.is_muted && <BellOff className="h-3 w-3 text-muted-foreground flex-shrink-0" />}
                        <h3 className={`truncate ${!chat.is_muted && chat.unread_count > 0 ? "font-bold" : "font-semibold"}`}>
                          {chat.is_group ? chat.name : chat.other_participant?.name}
                        </h3>
                      </div>
                      {chat.last_message && (
                        <span className="text-xs text-muted-foreground flex-shrink-0">
                          {formatDistanceToNow(new Date(chat.last_message.created_at), {
                            addSuffix: false,
                          })}
                        </span>
                      )}
                    </div>
                    {/* Row 2: preview (left) + unread badge (right) */}
                    <div className="flex items-center justify-between gap-2">
                      <p className={`text-sm line-clamp-1 flex-1 min-w-0 ${!chat.is_muted && chat.unread_count > 0 ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                        {chat.last_message ? getPreviewText(chat.last_message) : ''}
                      </p>
                      {chat.unread_count > 0 && (
                        <Badge
                          variant="default"
                          className={`h-5 min-w-5 px-1.5 text-xs flex-shrink-0 ${chat.is_muted ? 'opacity-50' : ''}`}
                        >
                          {chat.unread_count}
                        </Badge>
                      )}
                    </div>
                  </div>
                  {!chat.is_group && chat.other_participant?.is_online && (
                    <div className="w-2 h-2 rounded-full bg-accent" />
                  )}
                </button>
                {/* Action buttons — Archive + Delete only to prevent overflow on narrow screens.
                    Pin / Mute are accessible from inside the chat header. */}
                <div className="flex items-center gap-0.5 flex-shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleArchiveChat(chat.id, chat.is_archived);
                    }}
                    title={chat.is_archived ? t('unarchiveChat') : t('archiveChat')}
                  >
                    {chat.is_archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                    onClick={(e) => {
                      e.stopPropagation();
                      setChatToDelete(chat);
                    }}
                    disabled={deletingChatId === chat.id}
                  >
                    {deletingChatId === chat.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            ))}
            {!showArchived && pendingInvitations.map((invitation) => (
              <div
                key={`inv-${invitation.id}`}
                className="flex items-center gap-3 px-4 py-4 opacity-50"
              >
                <Avatar>
                  <AvatarFallback className="bg-muted text-muted-foreground">
                    <Send className="h-4 w-4" />
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-semibold text-muted-foreground">{invitation.invited_phone}</h3>
                    <span className="text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(invitation.created_at), {
                        addSuffix: true,
                      })}
                    </span>
                  </div>
                  <p className={`text-sm italic ${
                    invitation.delivery_status === 'delivered'
                      ? 'text-green-600 dark:text-green-400'
                      : invitation.delivery_status === 'undelivered' || invitation.delivery_status === 'failed'
                      ? 'text-destructive'
                      : 'text-muted-foreground'
                  }`}>
                    {invitation.delivery_status === 'delivered'
                      ? 'Delivered — waiting for them to sign up'
                      : invitation.delivery_status === 'undelivered' || invitation.delivery_status === 'failed'
                      ? `Not delivered${invitation.twilio_error_code ? ` (Twilio ${invitation.twilio_error_code})` : ''}`
                      : invitation.delivery_status === 'sent'
                      ? 'Sent to carrier…'
                      : t('invitationPending')}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-primary hover:text-primary flex-shrink-0"
                  onClick={() => handleResendInvite(invitation)}
                  disabled={resendingInviteId === invitation.id}
                >
                  {resendingInviteId === invitation.id ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-1" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-1" />
                  )}
                  {resendingInviteId === invitation.id ? t('resending') : t('resendInvite')}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-destructive hover:text-destructive hover:bg-destructive/10 flex-shrink-0"
                  onClick={() => setInviteToCancel(invitation)}
                  disabled={cancellingInviteId === invitation.id}
                >
                  {cancellingInviteId === invitation.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                </Button>
              </div>
            ))}
          </div>
        )}
      </ScrollArea>

      <AlertDialog open={!!chatToDelete} onOpenChange={() => setChatToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deleteChat')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('deleteChatConfirm')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteChat}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!inviteToCancel} onOpenChange={() => setInviteToCancel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('cancelInvite')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('cancelInviteConfirm')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelInvite}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <StatusDialog
        open={showStatus}
        onOpenChange={(v) => { setShowStatus(v); if (!v) setStatusUserId(undefined); }}
        initialUserId={statusUserId}
      />

      <div className="fixed bottom-[max(env(safe-area-inset-bottom),1.5rem)] right-6 flex flex-col gap-3 z-50">
        <Button
          size="icon"
          className="h-14 w-14 rounded-full shadow-lg"
          onClick={() => navigate('/create-group')}
          variant="secondary"
        >
          <MessagesSquare className="h-6 w-6" />
        </Button>
        <Button
          size="icon"
          className="h-14 w-14 rounded-full shadow-lg"
          onClick={() => navigate('/add-contact')}
        >
          <Plus className="h-6 w-6" />
        </Button>
      </div>
    </div>
  );
};

export default ChatList;
